import { Routes, UrlMatchResult, UrlSegment } from "@angular/router";

/**
 * Redirects from legacy Corral MVC URLs to their migrated Angular equivalents
 * (LTINFO-2993 bookmarks, LTINFO-3023 email links).
 *
 * TRPA users and at least one partner site have bookmarked and hard-linked the old
 * /Controller/Action/{param} URLs, and every notification email sent before the migration embedded
 * them too. Those shapes match no Angular route, so without these entries every such hit falls
 * through to the `**` catch-all and lands on the "Aw Shucks" not-found page. Emails already in an
 * inbox can't be rewritten, which makes a redirect the only available fix for them.
 *
 * Two things make a plain `redirectTo` route insufficient:
 *   1. The incoming path casing is unreliable (the first segment arrives lowercased, later ones don't),
 *      and Angular's `path` matching is case-sensitive -- hence the custom case-insensitive matchers.
 *   2. A plain string `redirectTo` can't substitute route params, so we route to LegacyRedirectComponent
 *      and let it do the token substitution (and, for name-keyed URLs, an async name -> ID lookup).
 *
 * Legacy areas were host-based rather than path-prefixed (eip./parcels./thresholds. subdomains), so
 * legacy URLs carry no area prefix. That also means these matchers are live on every subdomain; real
 * legacy traffic arrives on the correct host, so we don't gate them on SubdomainService.
 */

/** Param names, kept in sync with `routeParams` in app.routes.ts. Declared locally to avoid a cyclic import. */
const params = {
    projectID: "projectID",
    projectNumber: "projectNumber",
    parcelNumber: "parcelNumber",
    thresholdCategoryID: "thresholdCategoryID",
    thresholdCategoryName: "thresholdCategoryName",
    thresholdReportingCategoryID: "thresholdReportingCategoryID",
    thresholdReportingCategoryName: "thresholdReportingCategoryName",
    thresholdIndicatorID: "thresholdIndicatorID",
    thresholdEvaluationPeriodID: "thresholdEvaluationPeriodID",
    thresholdEvaluationYear: "thresholdEvaluationYear",
    mooringRegistrationSubmissionID: "mooringRegistrationSubmissionID",
    mooringPermitApplicationID: "mooringPermitApplicationID",
    // The renewal route names its param `submissionID`, not `mooringRegistrationSubmissionID`.
    submissionID: "submissionID",
    // Shared by three migrated invoice payment routes over three disjoint ID spaces. Each payment
    // component resolves the ID against its own invoice type, so a wrong-type ID 404s rather than
    // landing on someone else's invoice.
    invoiceID: "invoiceID",
};

interface LegacyRedirect {
    /** Legacy MVC controller name without the "Controller" suffix, e.g. "Project". Matched case-insensitively. */
    controller: string;
    /** Legacy MVC action name, e.g. "FactSheet". Matched case-insensitively. */
    action: string;
    /**
     * Names for the trailing URL segments, in order. The matcher requires exactly
     * 2 + paramNames.length segments, so optional-param legacy routes get one entry per variant.
     * Use null for a segment that is captured but intentionally discarded.
     */
    paramNames?: (string | null)[];
    /** Target path, with `:paramName` tokens substituted by LegacyRedirectComponent. */
    redirectTo: string;
}

/**
 * Builds a case-insensitive matcher for `/{controller}/{action}[/{param}...]`.
 *
 * Each param is a single segment, which safely covers dotted project numbers (01.01.01.0080) and
 * hyphenated APNs (117-100-037).
 */
export function legacyUrlMatcher(controller: string, action: string, paramNames: (string | null)[] = []) {
    const expectedLength = 2 + paramNames.length;

    return (segments: UrlSegment[]): UrlMatchResult | null => {
        if (segments.length !== expectedLength) return null;
        if (segments[0].path.toLowerCase() !== controller.toLowerCase()) return null;
        if (segments[1].path.toLowerCase() !== action.toLowerCase()) return null;

        const posParams: { [name: string]: UrlSegment } = {};
        paramNames.forEach((name, i) => {
            if (name) posParams[name] = segments[2 + i];
        });

        return { consumed: segments, posParams };
    };
}

const legacyRedirects: LegacyRedirect[] = [
    // ---------------------------------------------------------------- EIP (eip subdomain)
    // A partner site still hard-links the Fact Sheet URL; the rest are common bookmarks.
    { controller: "Project", action: "FactSheet", paramNames: [params.projectNumber], redirectTo: `/projects/fact-sheet/:${params.projectNumber}` },
    { controller: "Project", action: "FactSheetForPDF", paramNames: [params.projectNumber], redirectTo: `/projects/fact-sheet/:${params.projectNumber}` },
    { controller: "Project", action: "Summary", paramNames: [params.projectNumber], redirectTo: `/projects/fact-sheet/:${params.projectNumber}` },
    { controller: "Project", action: "Detail", paramNames: [params.projectID], redirectTo: `/projects/:${params.projectID}` },

    // ---------------------------------------------------------------- Parcel Tracker (parcels subdomain)
    // Note the segment reorder: legacy put the action before the parcel number, the new route appends it.
    { controller: "Parcel", action: "FactSheet", paramNames: [params.parcelNumber], redirectTo: `/parcels/:${params.parcelNumber}/fact-sheet` },
    { controller: "Parcel", action: "Detail", paramNames: [params.parcelNumber], redirectTo: `/parcels/:${params.parcelNumber}` },
    { controller: "Parcel", action: "Summary", paramNames: [params.parcelNumber], redirectTo: `/parcels/:${params.parcelNumber}` },
    // Legacy MooringRegistrationController.Welcome() was the Mooring Registration homepage.
    { controller: "MooringRegistration", action: "Welcome", redirectTo: "/moorings/home" },

    // ------------------------------------------------- Mooring email links (LTINFO-3023)
    // Every URL below appeared in a mooring notification email, so it is sitting in someone's inbox
    // and cannot be updated after the fact. These are the links that ask an applicant to *act* --
    // pay an invoice, sign a permit, supply missing information -- and the renewal notices recur
    // annually, so they keep getting clicked.
    //
    // The list was built by mining every href out of dbo.Notification.NotificationContent, which
    // stores the rendered body of all ~58k emails ever sent, rather than by reading the legacy
    // notification classes. That matters: several high-volume shapes come from controllers renamed
    // years ago and are invisible to a source-only inventory. Legacy kept dedicated shim
    // controllers for them -- MooringRegistrationController.Detail,
    // MooringRegistrationApplicationController, MooringPermitInvoiceController -- each commented
    // "Redirect to renamed controller since this path was included in emails to users". The entries
    // marked "legacy shim" below are ports of those, verified against the notification join tables
    // to carry the same primary key as the current route (no ID translation needed).
    {
        controller: "MooringRegistrationSubmission",
        action: "Detail",
        paramNames: [params.mooringRegistrationSubmissionID],
        redirectTo: `/moorings/registrations/:${params.mooringRegistrationSubmissionID}`,
    },
    // Legacy shim. The highest-volume registration link in the entire sent-mail history (~5.3k).
    {
        controller: "MooringRegistration",
        action: "Detail",
        paramNames: [params.mooringRegistrationSubmissionID],
        redirectTo: `/moorings/registrations/:${params.mooringRegistrationSubmissionID}`,
    },
    // Legacy shim, for the generation of naming before MooringRegistrationController.
    {
        controller: "MooringRegistrationApplication",
        action: "Detail",
        paramNames: [params.mooringRegistrationSubmissionID],
        redirectTo: `/moorings/registrations/:${params.mooringRegistrationSubmissionID}`,
    },
    { controller: "MooringRegistrationApplication", action: "Welcome", redirectTo: "/moorings/home" },
    // The "My Registrations" link in the footer of every mooring renewal reminder.
    { controller: "MooringRegistrationSubmission", action: "MyMooringRegistrationSubmissions", redirectTo: "/moorings/registrations/my-submissions" },
    // The "additional information required" link. Legacy landed on a single edit page; the migrated
    // equivalent is a workflow outlet, so target its first step explicitly rather than relying on
    // the outlet's default child redirect.
    {
        controller: "MooringRegistrationSubmission",
        action: "ApplicantInfo",
        paramNames: [params.mooringRegistrationSubmissionID],
        redirectTo: `/moorings/registration/edit/:${params.mooringRegistrationSubmissionID}/applicant-information`,
    },
    // Legacy never shimmed this one, so it has been dead since the controller rename. ~560 emails
    // carry it; redirecting it does better than legacy did.
    {
        controller: "MooringRegistration",
        action: "ApplicantInfo",
        paramNames: [params.mooringRegistrationSubmissionID],
        redirectTo: `/moorings/registration/edit/:${params.mooringRegistrationSubmissionID}/applicant-information`,
    },
    { controller: "MooringRegistrationPeriodInvoice", action: "Payment", paramNames: [params.invoiceID], redirectTo: `/moorings/invoices/:${params.invoiceID}/payment` },
    {
        controller: "MooringPermitApplicationInvoice",
        action: "Payment",
        paramNames: [params.invoiceID],
        redirectTo: `/moorings/permit-invoices/:${params.invoiceID}/payment`,
    },
    // Legacy shim.
    { controller: "MooringPermitInvoice", action: "Payment", paramNames: [params.invoiceID], redirectTo: `/moorings/permit-invoices/:${params.invoiceID}/payment` },
    // The "review and sign the TRPA permit" link -- the highest-value applicant action in the set.
    {
        controller: "MooringPermit",
        action: "ViewPermit",
        paramNames: [params.mooringPermitApplicationID],
        redirectTo: `/moorings/permit-applications/:${params.mooringPermitApplicationID}/view`,
    },
    { controller: "MooringRegistrationSubmission", action: "New", redirectTo: "/moorings/registration/new" },
    { controller: "MooringRegistrationSubmission", action: "PayMooringFees", redirectTo: "/moorings/pay-fees" },
    { controller: "MooringRegistrationRenewal", action: "Renew", paramNames: [params.submissionID], redirectTo: `/moorings/renew/:${params.submissionID}` },
    {
        controller: "MooringRegistrationSubmissionReview",
        action: "RegistrationReview",
        paramNames: [params.mooringRegistrationSubmissionID],
        redirectTo: `/moorings/registrations/:${params.mooringRegistrationSubmissionID}/review`,
    },

    // ---------------------------------------------------------------- Threshold (thresholds subdomain)
    { controller: "Home", action: "Index", redirectTo: "/" },
    { controller: "ThresholdCategory", action: "Index", redirectTo: "/threshold" },
    { controller: "ThresholdIndicator", action: "Search", redirectTo: "/threshold/search" },
    // Legacy carried the search criteria in the URL; the migrated search page has no equivalent, so drop it.
    { controller: "ThresholdIndicator", action: "Search", paramNames: [null], redirectTo: "/threshold/search" },
    {
        controller: "ThresholdIndicator",
        action: "Detail",
        paramNames: [params.thresholdIndicatorID],
        redirectTo: `/threshold/indicators/:${params.thresholdIndicatorID}`,
    },
    // The "2023 Evals" URL. The migrated component accepts either a year or a period ID in this slot
    // (it branches on value > 2000), so the legacy year passes straight through.
    {
        controller: "ThresholdEvaluation",
        action: "Index",
        paramNames: [params.thresholdEvaluationYear],
        redirectTo: `/threshold/evaluations/:${params.thresholdEvaluationYear}`,
    },
    { controller: "ThresholdEvaluation", action: "Index", redirectTo: "/threshold/evaluations" },
    { controller: "ThresholdEvaluation", action: "PastEvaluations", redirectTo: "/threshold/evaluations" },
    {
        controller: "ThresholdEvaluation",
        action: "ThresholdEvaluationPeriodDetail",
        paramNames: [params.thresholdEvaluationPeriodID],
        redirectTo: `/threshold/past-evaluations/:${params.thresholdEvaluationPeriodID}`,
    },
    // Legacy variant carrying a tab name. The migrated page is one scrolling dashboard, so the tab is dropped.
    {
        controller: "ThresholdEvaluation",
        action: "ThresholdEvaluationPeriodDetail",
        paramNames: [params.thresholdEvaluationPeriodID, null],
        redirectTo: `/threshold/past-evaluations/:${params.thresholdEvaluationPeriodID}`,
    },
    { controller: "ThresholdEvaluation", action: "HowThresholdsAreEvaluated", redirectTo: "/threshold/how-thresholds-are-evaluated" },
    // Cumulative accounting and compliance measures are year-keyed on both sides, despite the new route
    // naming its param thresholdEvaluationPeriodID.
    {
        controller: "CumulativeAccounting",
        action: "Index",
        paramNames: [params.thresholdEvaluationYear],
        redirectTo: `/threshold/cumulative-accounting/:${params.thresholdEvaluationYear}`,
    },
    { controller: "CumulativeAccounting", action: "Index", redirectTo: "/threshold/cumulative-accounting" },
    {
        controller: "ComplianceMeasure",
        action: "Index",
        paramNames: [params.thresholdEvaluationYear],
        redirectTo: `/threshold/compliance-measures/:${params.thresholdEvaluationYear}`,
    },
    { controller: "ComplianceMeasure", action: "Index", redirectTo: "/threshold/compliance-measures" },
    // These two legacy URLs keyed off the entity name. Rather than look the name up here, the migrated
    // detail routes accept either an ID or a name in that slot (see threshold-route-key.ts), so the
    // legacy name passes straight through and stays in the address bar.
    {
        controller: "ThresholdCategory",
        action: "Detail",
        paramNames: [params.thresholdCategoryName],
        redirectTo: `/threshold/categories/:${params.thresholdCategoryName}`,
    },
    {
        controller: "ThresholdReportingCategory",
        action: "Detail",
        paramNames: [params.thresholdReportingCategoryName],
        redirectTo: `/threshold/reporting-categories/:${params.thresholdReportingCategoryName}`,
    },
];

export const legacyRedirectRoutes: Routes = legacyRedirects.map((redirect) => ({
    matcher: legacyUrlMatcher(redirect.controller, redirect.action, redirect.paramNames),
    data: { redirectTo: redirect.redirectTo },
    loadComponent: () => import("./pages/legacy-redirect/legacy-redirect.component").then((m) => m.LegacyRedirectComponent),
}));
